/** Shared browser download helpers — keep filenames + extensions correct across admin portal. */

const MIME_TO_EXT: Record<string, string> = {
  'application/pdf': '.pdf',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
  'application/vnd.ms-excel': '.xls',
  'text/csv': '.csv',
  'text/plain': '.txt',
  'text/html': '.html',
  'application/json': '.json',
  'image/png': '.png',
  'image/jpeg': '.jpg',
  'image/jpg': '.jpg',
  'image/webp': '.webp',
  'image/gif': '.gif',
  'application/zip': '.zip',
};

export function extensionForContentType(contentType?: string | null): string | undefined {
  if (!contentType) return undefined;
  const base = contentType.split(';')[0].trim().toLowerCase();
  return MIME_TO_EXT[base];
}

export function ensureFilenameExtension(
  filename: string,
  contentType?: string | null,
  fallbackExt?: string,
): string {
  const cleaned =
    String(filename || 'download')
      .replace(/[/\\?%*:|"<>]/g, '_')
      .trim() || 'download';

  if (/\.[a-z0-9]{1,8}$/i.test(cleaned)) {
    return cleaned;
  }

  const ext =
    extensionForContentType(contentType) ||
    (fallbackExt
      ? fallbackExt.startsWith('.')
        ? fallbackExt
        : `.${fallbackExt}`
      : '') ||
    '';

  return ext ? `${cleaned}${ext}` : cleaned;
}

/** Parse Content-Disposition; prefer RFC 5987 filename*. */
export function filenameFromContentDisposition(
  header: string | null | undefined,
  fallback: string,
): string {
  if (!header) return fallback;

  const utfMatch = header.match(/filename\*=(?:UTF-8''|utf-8'')([^;]+)/i);
  if (utfMatch?.[1]) {
    try {
      return decodeURIComponent(utfMatch[1].trim().replace(/^"+|"+$/g, ''));
    } catch {
      /* fall through */
    }
  }

  const quoted = header.match(/filename="([^"]+)"/i);
  if (quoted?.[1]) return quoted[1];

  const unquoted = header.match(/filename=([^;]+)/i);
  if (unquoted?.[1]) return unquoted[1].trim().replace(/^"+|"+$/g, '');

  return fallback;
}

/** Trigger a file save from an in-memory Blob with a guaranteed extension. */
export function triggerBlobDownload(
  blob: Blob,
  filename: string,
  fallbackExt?: string,
): string {
  const name = ensureFilenameExtension(filename, blob.type, fallbackExt);
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
  return name;
}

/**
 * Download from a fetch Response using Content-Disposition + Content-Type when present.
 */
export async function downloadFromResponse(
  response: Response,
  fallbackFilename: string,
  fallbackExt?: string,
): Promise<string> {
  const blob = await response.blob();
  const fromHeader = filenameFromContentDisposition(
    response.headers.get('Content-Disposition'),
    fallbackFilename,
  );
  const contentType =
    response.headers.get('Content-Type') || blob.type || undefined;
  const name = ensureFilenameExtension(fromHeader, contentType, fallbackExt);
  return triggerBlobDownload(blob, name, fallbackExt);
}

/**
 * Fetch a remote/same-origin URL as a blob and save with an explicit filename.
 * Fixes cross-origin `<a download>` (ignored by browsers) that otherwise save
 * UUID/path segments without the correct extension.
 */
export async function downloadRemoteFile(
  url: string,
  filename: string,
  options?: { contentType?: string; fallbackExt?: string; headers?: HeadersInit },
): Promise<string> {
  const token = typeof localStorage !== 'undefined' ? localStorage.getItem('accessToken') : null;
  const headers: Record<string, string> = {
    ...((options?.headers as Record<string, string>) || {}),
  };
  if (token && !headers.Authorization && !headers.authorization) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(url, {
    headers,
    credentials: 'include',
  });
  if (!response.ok) {
    throw new Error(`Download failed (${response.status})`);
  }
  const blob = await response.blob();
  const contentType =
    options?.contentType ||
    response.headers.get('Content-Type') ||
    blob.type ||
    undefined;
  const name = ensureFilenameExtension(
    filenameFromContentDisposition(
      response.headers.get('Content-Disposition'),
      filename,
    ),
    contentType,
    options?.fallbackExt,
  );
  return triggerBlobDownload(
    contentType && !blob.type
      ? new Blob([blob], { type: contentType })
      : blob,
    name,
    options?.fallbackExt,
  );
}
